import { devDebug } from "./devLog";
// End-to-end encryption for direct messages.
//
// Design:
//  - Both peers derive a deterministic X25519 keypair from their BIP39
//    mnemonic (SHA-256(seed || context) → clamped private scalar).
//  - The X25519 public key is published on the user's profile server-side.
//  - Sender computes the shared secret via ECDH, derives an AES-GCM key
//    via HKDF-like SHA-256 with a fixed info string, and encrypts each
//    message with a fresh 12-byte random nonce.
//  - The server stores only the base64 ciphertext + nonce; it has no
//    means to decrypt the payload.
//
// This is a pragmatic MVP: no ratcheting or forward secrecy. Compromise
// of a mnemonic exposes past conversations. Upgrading to a proper
// double-ratchet is left as future work.

import { x25519 } from "@noble/curves/ed25519.js";
import { sha256 } from "@noble/hashes/sha2.js";
import { mnemonicToSeedSync } from "@scure/bip39";
import { unlockMnemonic, signMessage, deriveKeypair } from "./identity";
import { verifyAsync } from "@noble/ed25519";

const CTX_PRIV = "OpinioneLibera:x25519:v1";
const CTX_HKDF = "OpinioneLibera:DM-AES-GCM:v1";
const DM_BINDING_PREFIX = "OpinioneLibera:DM-Pubkey:v1|";

/** Domain-separated message signed by Ed25519 identity to bind x25519 DM key. */
export function dmKeyBindingMessage(x25519PublicKeyHex) {
  return DM_BINDING_PREFIX + x25519PublicKeyHex.toLowerCase();
}

function bytesToHex(bytes) {
  let out = "";
  for (const b of bytes) out += b.toString(16).padStart(2, "0");
  return out;
}
function hexToBytes(hex) {
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}
function bytesToB64(bytes) {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}
function b64ToBytes(b64) {
  const s = atob(b64);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}

function concat(a, b) {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0);
  out.set(b, a.length);
  return out;
}

// Derives the X25519 keypair from the mnemonic. Deterministic, so the same
// user always ends up with the same DM keys across devices/logins.
export function deriveX25519Sync(mnemonic) {
  const seed = mnemonicToSeedSync(mnemonic, "OpinioneLibera");
  const scalar = sha256(concat(seed, new TextEncoder().encode(CTX_PRIV)));
  // x25519.getPublicKey handles clamping internally.
  const pub = x25519.getPublicKey(scalar);
  return {
    privateKeyHex: bytesToHex(scalar),
    publicKeyHex: bytesToHex(pub),
  };
}

// Cached per-session so we don't re-derive on every message.
let cachedPriv = null;
let cachedPub = null;
let cachedSig = null;

export async function getMyDmKeys() {
  if (cachedPriv && cachedPub) return { privateKeyHex: cachedPriv, publicKeyHex: cachedPub };
  const mnemonic = await unlockMnemonic();
  if (!mnemonic) throw new Error("Impossibile sbloccare la chiave privata");
  const keys = deriveX25519Sync(mnemonic);
  cachedPriv = keys.privateKeyHex;
  cachedPub = keys.publicKeyHex;
  return keys;
}

/**
 * Returns the x25519 keypair together with the Ed25519 signature that
 * binds the public part to the caller's identity (see SEC-001). The
 * signature is deterministic — same inputs always produce the same
 * hex — so callers can trust it after computing it once per session.
 */
export async function getMyDmKeysSigned() {
  if (cachedPriv && cachedPub && cachedSig) {
    return { privateKeyHex: cachedPriv, publicKeyHex: cachedPub, signatureHex: cachedSig };
  }
  const mnemonic = await unlockMnemonic();
  if (!mnemonic) throw new Error("Impossibile sbloccare la chiave privata");
  const dm = deriveX25519Sync(mnemonic);
  const identity = await deriveKeypair(mnemonic.split(" "));
  const sig = await signMessage(identity.privateKeyHex, dmKeyBindingMessage(dm.publicKeyHex));
  cachedPriv = dm.privateKeyHex;
  cachedPub = dm.publicKeyHex;
  cachedSig = sig;
  return { privateKeyHex: dm.privateKeyHex, publicKeyHex: dm.publicKeyHex, signatureHex: sig };
}

export function clearDmKeyCache() {
  cachedPriv = null;
  cachedPub = null;
  cachedSig = null;
}

/**
 * Verifies that the peer's x25519 DM key was signed by their Ed25519
 * identity key. Returns true if the binding is valid (SEC-001).
 */
export async function verifyPeerDmKeyBinding({ publicKeyHex, x25519PublicKeyHex, x25519SignatureHex }) {
  if (!publicKeyHex || !x25519PublicKeyHex || !x25519SignatureHex) return false;
  if (!/^[0-9a-f]{64}$/i.test(publicKeyHex)) return false;
  if (!/^[0-9a-f]{64}$/i.test(x25519PublicKeyHex)) return false;
  if (!/^[0-9a-f]{128}$/i.test(x25519SignatureHex)) return false;
  try {
    const msg = new TextEncoder().encode(dmKeyBindingMessage(x25519PublicKeyHex));
    return await verifyAsync(hexToBytes(x25519SignatureHex), msg, hexToBytes(publicKeyHex));
  } catch (err) {
    devDebug("verifyPeerDmKeyBinding failed", err?.message);
    return false;
  }
}

/**
 * SEC-001 enforcing gate (iter55). "Trust On First Signature" semantics:
 *   • Peer has published a signature  → MUST verify. Throws PeerBindingError
 *     if invalid so caller MUST NOT proceed (encryption to unverified key
 *     would be silently readable by a compromised server).
 *   • Peer is fully unsigned (legacy, pre-iter45)  → allow; caller shows
 *     an amber "non verificato" badge. This closes the MITM window for
 *     everyone who has already migrated, without breaking legacy chats.
 * Call this synchronously before ANY encrypt/decrypt DM operation.
 */
export class PeerBindingError extends Error {
  constructor(msg) { super(msg); this.name = "PeerBindingError"; }
}
export async function requirePeerBinding(peer) {
  if (!peer) throw new PeerBindingError("Peer mancante");
  if (!peer.x25519PublicKeyHex) throw new PeerBindingError("Peer senza chiave DM (non può ricevere messaggi cifrati)");
  // Legacy peer without any signature yet → let it through (graceful migration).
  if (!peer.x25519SignatureHex || !peer.publicKeyHex) return "unsigned";
  const ok = await verifyPeerDmKeyBinding(peer);
  if (!ok) {
    throw new PeerBindingError(
      "La chiave DM del contatto NON è firmata correttamente dalla sua identità. Rifiuto di procedere (SEC-001). Il contatto potrebbe essere stato manomesso: contattalo su un altro canale.",
    );
  }
  return "verified";
}

/**
 * Safety number for out-of-band verification, à la Signal.
 * Deterministic across both peers: SHA-256 of the two x25519 pubkeys
 * concatenated in canonical (sorted) order, formatted as 5 groups of
 * 5 digits (base-10, 25 digits total) drawn from consecutive byte pairs.
 */
export function computeSafetyNumber(x25519A, x25519B) {
  if (!x25519A || !x25519B) return "";
  const a = x25519A.toLowerCase();
  const b = x25519B.toLowerCase();
  const [lo, hi] = a < b ? [a, b] : [b, a];
  const digest = sha256(concat(hexToBytes(lo), hexToBytes(hi)));
  // Take the first 10 bytes → 5 groups of 2 bytes → 5 decimal chunks of 5 digits.
  let out = "";
  for (let i = 0; i < 5; i++) {
    const hi16 = (digest[i * 2] << 8) | digest[i * 2 + 1];
    const chunk = String(hi16).padStart(5, "0");
    out += (i > 0 ? " " : "") + chunk;
  }
  return out;
}

async function deriveAesKey(ourPrivHex, theirPubHex) {
  const shared = x25519.getSharedSecret(hexToBytes(ourPrivHex), hexToBytes(theirPubHex));
  const km = sha256(concat(shared, new TextEncoder().encode(CTX_HKDF)));
  return crypto.subtle.importKey(
    "raw",
    km,
    { name: "AES-GCM" },
    false,
    ["encrypt", "decrypt"],
  );
}

export async function encryptForPeer(peerX25519PubHex, plaintext) {
  const { privateKeyHex } = await getMyDmKeys();
  const key = await deriveAesKey(privateKeyHex, peerX25519PubHex);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(plaintext),
  );
  return {
    ciphertextB64: bytesToB64(new Uint8Array(ct)),
    nonceB64: bytesToB64(iv),
  };
}

/**
 * Higher-level wrapper: refuses to encrypt (or decrypt) unless the peer's
 * DM key is cryptographically bound to their identity. Callers pass the
 * whole `peer` object (from the DM API); we verify the Ed25519 signature
 * before doing anything. Returns `null` when the binding is missing or
 * invalid — the UI should show a "chiave non verificata" warning.
 */
export async function assertPeerBinding(peer) {
  if (!peer) return false;
  if (!peer.x25519PublicKeyHex || !peer.x25519SignatureHex || !peer.publicKeyHex) {
    return false;
  }
  return await verifyPeerDmKeyBinding(peer);
}

export async function decryptFromPeer(peerX25519PubHex, ciphertextB64, nonceB64) {
  try {
    const { privateKeyHex } = await getMyDmKeys();
    const key = await deriveAesKey(privateKeyHex, peerX25519PubHex);
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: b64ToBytes(nonceB64) },
      key,
      b64ToBytes(ciphertextB64),
    );
    return new TextDecoder().decode(pt);
  } catch {
    return null; // undecipherable — key mismatch, corruption, or forgery
  }
}

// --- Binary variant used for voice DMs -----------------------------------
// The audio payload is encrypted with the same peer-derived AES-GCM key.
// The output is raw bytes (Uint8Array) — the caller uploads them to the
// audio object store as `audio/webm` bytes and sends the resulting
// objectKey + IV inside a normal (text) DM ciphertext.

export async function encryptBytesForPeer(peerX25519PubHex, bytes) {
  const { privateKeyHex } = await getMyDmKeys();
  const key = await deriveAesKey(privateKeyHex, peerX25519PubHex);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, bytes);
  return { ciphertextBytes: new Uint8Array(ct), nonceB64: bytesToB64(iv) };
}

export async function decryptBytesFromPeer(peerX25519PubHex, ciphertextBytes, nonceB64) {
  try {
    const { privateKeyHex } = await getMyDmKeys();
    const key = await deriveAesKey(privateKeyHex, peerX25519PubHex);
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: b64ToBytes(nonceB64) },
      key,
      ciphertextBytes,
    );
    return new Uint8Array(pt);
  } catch {
    return null;
  }
}
